import { DocumentBlock } from '@/schemas/schema';
import { StyleSheet, Text, View } from '@react-pdf/renderer';
import React from 'react';

// Styles for different block types
const styles = StyleSheet.create({
  // Container styles
  article: {
    marginTop: 15,
    marginBottom: 10,
    width: '100%',
  },
  section: {
    marginTop: 10,
    marginBottom: 8,
    width: '100%',
  },

  // Text styles
  articleTitle: {
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 8,
    textTransform: 'uppercase',
    borderBottom: '1pt solid #000',
    paddingBottom: 2,
  },
  sectionTitle: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 6,
  },
  paragraph: {
    fontSize: 10,
    fontFamily: 'Helvetica',
    marginBottom: 8,
    lineHeight: 1.5,
    textAlign: 'justify',
  },

  // List styles
  list: {
    marginBottom: 8,
  },
  listItem: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  listMarker: {
    width: 25,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  listContent: {
    flex: 1,
    fontSize: 10,
    fontFamily: 'Helvetica',
    lineHeight: 1.5,
  },

  // Definition styles
  definitionContainer: {
    marginBottom: 8,
    paddingLeft: 10,
    borderLeft: '2pt solid #eee',
  },
  definitionItem: {
    marginBottom: 6,
  },
  definitionTerm: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 2,
  },
  definitionDef: {
    fontSize: 10,
    fontFamily: 'Helvetica',
    lineHeight: 1.5,
  },

  // Table styles
  table: {
    marginBottom: 12,
    borderWidth: 0.5,
    borderColor: '#d1d5db',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#d1d5db',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#f3f4f6',
    borderBottomWidth: 1,
    borderBottomColor: '#9ca3af',
  },
  tableCell: {
    flex: 1,
    padding: 6,
    fontSize: 9,
    fontFamily: 'Helvetica',
  },
  tableHeaderCell: {
    flex: 1,
    padding: 6,
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
  },
});

interface BlockRendererProps {
  block: DocumentBlock;
  level?: number;
}

export const BlockRenderer: React.FC<BlockRendererProps> = ({ block, level = 0 }) => {
  const { type, props = {}, children = [], text } = block;

  switch (type) {
    case 'article':
      return (
        <View style={styles.article}>
          <Text style={styles.articleTitle}>{props.title}</Text>
          {children.map((child, index) => (
            <BlockRenderer key={child.id || index} block={child} level={level + 1} />
          ))}
        </View>
      );

    case 'section':
      return (
        <View style={styles.section}>
          {props.title && <Text style={styles.sectionTitle}>{props.title}</Text>}
          {children.map((child, index) => (
            <BlockRenderer key={child.id || index} block={child} level={level + 1} />
          ))}
        </View>
      );

    case 'paragraph':
      return (
        <Text style={[styles.paragraph, props.style]}>
          {text}
          {children.map((child, index) => (
            <BlockRenderer key={child.id || index} block={child} level={level + 1} />
          ))}
        </Text>
      );

    case 'list':
      return (
        <View style={styles.list}>
          {children.map((child, index) => (
            <BlockRenderer key={child.id || index} block={child} level={level + 1} />
          ))}
        </View>
      );

    case 'list_item':
      return (
        <View style={styles.listItem}>
          <Text style={styles.listMarker}>{props.marker || '•'}</Text>
          <Text style={styles.listContent}>{text}</Text>
        </View>
      );

    case 'definition':
      return (
        <View style={styles.definitionContainer}>
          {children.map((child, index) => (
            <BlockRenderer key={child.id || index} block={child} level={level + 1} />
          ))}
        </View>
      );

    case 'definition_item':
      return (
        <View style={styles.definitionItem}>
          <Text style={styles.definitionTerm}>{props.term}</Text>
          <Text style={styles.definitionDef}>{text}</Text>
        </View>
      );

    case 'table':
      return (
        <View style={styles.table}>
          {children.map((child, index) => (
            <BlockRenderer key={child.id || index} block={child} level={level + 1} />
          ))}
        </View>
      );

    case 'table_row': {
      const isHeader = props.header === true;
      return (
        <View style={isHeader ? styles.tableHeaderRow : styles.tableRow}>
          {children.map((child, index) => (
            <BlockRenderer
              key={child.id || index}
              block={{ ...child, props: { ...child.props, isHeader } }}
              level={level + 1}
            />
          ))}
        </View>
      );
    }

    case 'table_cell':
      return <Text style={props.isHeader ? styles.tableHeaderCell : styles.tableCell}>{text}</Text>;

    case 'page_break':
      // Page breaks are handled at the document level, not in BlockRenderer
      // This is a placeholder that returns null
      return null;

    default:
      // Fallback for unknown blocks: just render children
      return (
        <View>
          {children.map((child, index) => (
            <BlockRenderer key={child.id || index} block={child} level={level + 1} />
          ))}
        </View>
      );
  }
};
